import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Lock, CheckCircle2 } from 'lucide-react';
import client, { apiErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await client.post('/auth/reset-password', { token, newPassword: password });
      setDone(true);
      toast.success('Password reset — please log in.');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="text-center">
          <div className="font-display text-3xl font-bold">Missing reset token</div>
          <p className="text-zinc-500 mt-2">Please use the link from your reset email.</p>
          <Link to="/admin/login" className="mt-6 inline-block underline font-mono">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-white p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded-md bg-zinc-950 text-white grid place-items-center font-display font-bold">S</div>
          <div>
            <div className="font-display text-lg font-bold leading-none">Stockanize</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Warehouse OS</div>
          </div>
        </div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Set a new password</div>
        <h1 className="font-display text-4xl font-bold text-zinc-950 mb-8 leading-tight">Choose a strong,<br />memorable password.</h1>

        {done ? (
          <div className="p-5 rounded-md border border-emerald-200 bg-emerald-50 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <div className="font-medium text-emerald-900">Password updated</div>
              <p className="text-sm text-emerald-800 mt-1">You can now sign in with your new password.</p>
              <div className="flex gap-3 mt-4">
                <Button asChild className="bg-zinc-950 hover:bg-zinc-800 text-white"><Link to="/admin/login">Super Admin login</Link></Button>
                <Button asChild variant="outline"><Link to="/warehouse/login">Warehouse login</Link></Button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">New password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
                <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-11 border-zinc-200" placeholder="Min 8 chars, upper+lower+digit" data-testid="reset-password-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-zinc-600">Confirm password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
                <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-9 h-11 border-zinc-200" data-testid="reset-confirm-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="reset-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
            </Button>
            <Link to="/admin/login" className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-950 mt-4">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
